import { z } from 'zod';

export interface IRegexField {
  pattern: RegExp;
  message: string;
}

interface IRegex {
  email: IRegexField;
  password: IRegexField;
  name: IRegexField;
  entityName: IRegexField;
}

export const RegexPatterns: IRegex = {
  email: {
    pattern:
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    message: 'Invalid Email Address',
  },
  password: {
    pattern: /^(.*)/, // /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{6,}$/,
    message:
      'Must contain at least one number & uppercase letter & at least 6 characters',
  },
  name: {
    pattern:
      /^[a-zA-ZÀ-ÿĀ-žА-я\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\s'-\\.]{0,75}$/,
    message: 'Name is invalid',
  },
  entityName: {
    pattern: /^[a-zA-Z0-9\s'"\\.,:;!@#$%^&*()_+={}[\]|\\~`<>?/-]{0,75}$/,
    message: 'Entity name is invalid',
  },
};

export const isEmail = z
  .string()
  .nonempty()
  .trim()
  .refine((value: string) => RegexPatterns.email.pattern.test(value), {
    message: RegexPatterns.email.message,
  });

export const isName = z
  .string()
  .nonempty({ message: 'Name is required' })
  .min(3, { message: 'Name must have at least 3 characters' })
  .trim()
  .refine((value: string) => RegexPatterns.name.pattern.test(value), {
    message: RegexPatterns.name.message,
  });

export const isEntityName = z
  .string()
  .nonempty({ message: 'Entity name is required' })
  .min(2, { message: 'Entity name must have at least 2 characters' })
  .max(75, { message: 'Entity name cannot be more than 75 characters' })
  .trim()
  .refine((value: string) => RegexPatterns.entityName.pattern.test(value), {
    message: RegexPatterns.entityName.message,
  });

export const isPassword = z
  .string()
  .nonempty()
  .refine((value: string) => RegexPatterns.password.pattern.test(value), {
    message: RegexPatterns.password.message,
  });

export const isOptionalEmail = z
  .string()
  .optional()
  .refine((value) => !value || RegexPatterns.email.pattern.test(value), {
    message: RegexPatterns.email.message,
  });

export const isOptionalName = z
  .string()
  .optional()
  .refine((value) => !value || RegexPatterns.name.pattern.test(value), {
    message: RegexPatterns.name.message,
  });

export const isOptionalEntityName = z
  .string()
  .optional()
  .refine((value) => !value || RegexPatterns.entityName.pattern.test(value), {
    message: RegexPatterns.entityName.message,
  });

export const isOptionalPassword = z
  .string()
  .optional()
  .refine((value) => !value || RegexPatterns.password.pattern.test(value), {
    message: RegexPatterns.password.message,
  });

export const locationSchema = z.object({
  coordinates: z.array(z.number()).optional(),
  address: z.string().nullable().optional(),
});

/** One invalid form field: its dotted path, such as `versions.0.charges.1.rate`, and its message. */
export interface FormErrorEntry {
  path: string;
  message: string;
}

const NON_FIELD_KEYS = new Set(['ref', 'types', 'type', 'message']);

/** Every message in a react-hook-form errors object, with the path of its field. */
export const listFormErrors = (errors: object, path = ''): FormErrorEntry[] => {
  const { message } = errors as { message?: unknown };
  const own =
    path && typeof message === 'string' && message ? [{ path, message }] : [];
  return [
    ...own,
    ...Object.entries(errors).flatMap(([key, value]) =>
      NON_FIELD_KEYS.has(key) || !value || typeof value !== 'object'
        ? []
        : listFormErrors(value, path ? `${path}.${key}` : key)
    ),
  ];
};

/** The first few invalid fields as `path: message` text, counting the rest. */
export const summariseFormErrors = (entries: FormErrorEntry[], limit = 3) =>
  [
    entries
      .slice(0, limit)
      .map(({ path, message }) => `${path}: ${message}`)
      .join('; '),
    entries.length > limit ? `(+${entries.length - limit} more)` : '',
  ]
    .filter(Boolean)
    .join(' ');
